import {
  SessionRole,
  useGetBulkSessionData,
  useGetSessionsByEventId,
} from '@sunrise-ui/api-client';
import type { SessionOutput } from '@sunrise-ui/api/events/api';
import { SessionStatus } from '@sunrise-ui/api/events/api';
import {
  Card,
  Colors,
  TextBold,
  TextReg,
  formatTime,
  formatTimeRange,
  isAndroid,
  isIos,
  useAuthStore,
  useDynamicStyles,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import { router } from 'expo-router';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  StyleSheet,
  TouchableOpacity,
  View,
  type ViewStyle,
} from 'react-native';

// Import session components directly to avoid circular dependencies
import { SessionCardTags } from './SessionCardTags';
import { SessionConfirmButton } from './SessionConfirmButton';
import { SessionEditButton } from './SessionEditButton';
import { SessionFavoriteButton } from './SessionFavoriteButton';
import { SessionShareButton } from './SessionShareButton';
import { SessionStatusMenuButton } from './SessionStatusMenuButton';
import { SessionVoteButton } from './SessionVoteButton';

// Import ProfileResult from profiles directory
import { ProfileResult } from '../profiles';

type Props = {
  eventId: string;
  sessionId: string;
  style?: ViewStyle;
  forceVoteButton?: boolean;
  isManagerView?: boolean;
  onVisible?: () => void;
  onInvisible?: () => void;
  communityId?: string;
  useCache?: boolean;
};

export function SessionCard({
  eventId,
  sessionId,
  style,
  forceVoteButton = false,
  isManagerView = false,
  onVisible,
  onInvisible,
  communityId,
}: Props) {
  const styles = useDynamicStyles(createStyles);
  const [cardWidth, setCardWidth] = useState(0);
  const isVisibleRef = useRef(false);

  // Handle visibility tracking for time-based viewed marking
  useEffect(() => {
    if (onVisible && !isVisibleRef.current) {
      isVisibleRef.current = true;
      onVisible();
    }

    return () => {
      if (onInvisible && isVisibleRef.current) {
        isVisibleRef.current = false;
        onInvisible();
      }
    };
  }, [onVisible, onInvisible]);

  // Get sessions from the sessions-by-event cache using the standard API hook
  // This hook has already been called in the session voting page so the data should be cached
  const { data: eventSessions, isLoading: isLoadingEventSessions } =
    useGetSessionsByEventId(eventId, false, communityId);

  // Find the specific session we're interested in
  const session = eventSessions?.find(
    (session: SessionOutput) => session.id === sessionId,
  );

  // Extract session status into descriptive boolean variables
  const isStatusVoting = session?.status === SessionStatus.Voting;
  const isStatusScheduled = session?.status === SessionStatus.Scheduled;
  const isStatusConfirmed = session?.status === SessionStatus.Confirmed;
  const isStatusScheduledOrConfirmed = isStatusScheduled || isStatusConfirmed;

  // Extract all session IDs for the bulk data fetch
  const sessionIds =
    eventSessions?.map((session) => session.id).filter(Boolean) || [];

  // Get bulk session data for attendees and other metadata
  // Use ALL sessionIds from the event to ensure full cache population
  const { data: bulkSessionData, isLoading: isLoadingBulkData } =
    useGetBulkSessionData({
      sessionIds,
    });

  // Extract attendee data from bulk query
  const bulkSessionInfo = bulkSessionData?.session_data?.find(
    (sessionData) => sessionData.session_id === sessionId,
  );

  const speakers = bulkSessionInfo?.data?.attendees?.filter(
    (a) => a.role === SessionRole.SPEAKER || a.role === SessionRole.MODERATOR,
  );

  // Simple loading state that covers all data requirements
  const isLoading = isLoadingEventSessions || isLoadingBulkData;

  // Check if session is currently ongoing (for pink border)
  const isSessionOngoing = useMemo(() => {
    if (!session?.start_date || !session?.end_date) return false;

    const now = DateTime.now();
    const startTime = DateTime.fromISO(session.start_date);
    const endTime = DateTime.fromISO(session.end_date);

    return now >= startTime && now <= endTime;
  }, [session?.start_date, session?.end_date]);

  // Check if session starts within 24 hours (for purple border)
  const isStartingWithin24Hours = useMemo(() => {
    if (!session?.start_date) return false;

    const now = DateTime.now();
    const startTime = DateTime.fromISO(session.start_date);

    // Only show purple if session hasn't started yet and starts within 24 hours
    if (startTime <= now) return false;

    const hoursUntilStart = startTime.diff(now, 'hours').hours;
    return hoursUntilStart <= 24;
  }, [session?.start_date]);

  const { currentUserId } = useAuthStore();

  // Safe access with proper typing
  // Allow editing if user is creator or a speaker, or in manager view
  const canEditSession = !!(
    isManagerView ||
    session?.user_id === currentUserId ||
    (speakers && speakers.some((speaker) => speaker.user_id === currentUserId))
  );
  const isOwner =
    session?.user_id === currentUserId ||
    (speakers && speakers.some((speaker) => speaker.user_id === currentUserId));

  // Determine if we should show the top-right vote button
  // Hide vote button entirely for scheduled sessions (different system forthcoming)
  // For manager view, keep vote button at top-right only for voting sessions
  const showTopRightVoteButton =
    !isStatusScheduledOrConfirmed &&
    (forceVoteButton || (!isManagerView && !forceVoteButton) || isStatusVoting);

  // Show favorite button for scheduled/confirmed sessions when vote button is hidden
  const showFavoriteButton = isStatusScheduledOrConfirmed && !isManagerView;

  // Use empty array placeholders when loading to trigger ListEmptyComponent
  const isLoadingAttendees = isLoadingBulkData;
  const speakersToShow = isLoadingAttendees ? [] : (speakers ?? []);

  // Extract speaker user IDs for notification purposes
  const speakerUserIds = speakers?.map((speaker) => speaker.user_id) || [];
  const canUseTwoColumns = cardWidth > 440;

  // Handler to open session details modal
  const handleCardPress = () => {
    router.setParams({ sessionId, eventId });
  };

  // Determine border color based on session timing
  const borderColor = isSessionOngoing
    ? Colors.pink0
    : isStartingWithin24Hours
      ? Colors.purple1
      : 'transparent';

  return (
    <Card
      style={[styles.sessionCard, { borderWidth: 1.5, borderColor }, style]}
      shadowColor={Colors.purple0}
      onLayout={(event) => {
        setCardWidth(event.nativeEvent.layout.width);
      }}
    >
      <TouchableOpacity
        style={styles.touchableWrapper}
        onPress={handleCardPress}
        activeOpacity={0.9}
      >
        <View style={styles.cardContent}>
          <View style={styles.topRow}>
            <View style={styles.leftColumn}>
              {isStatusScheduledOrConfirmed &&
                formatTimeRange(session?.start_date, session?.end_date) && (
                  <TextReg style={styles.timeRange}>
                    {formatTimeRange(session?.start_date, session?.end_date)}
                  </TextReg>
                )}
              <View style={styles.tagsWrapper}>
                <SessionCardTags sessionId={sessionId} eventId={eventId} />
              </View>
            </View>
            <View style={styles.buttonsContainer}>
              {showTopRightVoteButton && (
                <SessionVoteButton
                  sessionId={sessionId}
                  eventId={eventId}
                  communityId={communityId}
                />
              )}
              {showFavoriteButton && (
                <SessionFavoriteButton
                  sessionId={sessionId}
                  eventId={eventId}
                />
              )}
              <>
                {(isManagerView || !isStatusScheduledOrConfirmed) && (
                  <SessionEditButton
                    canEdit={canEditSession}
                    sessionId={sessionId}
                    eventId={eventId}
                    communityId={communityId}
                    style={styles.editButton}
                  />
                )}
                {isManagerView && (
                  <SessionStatusMenuButton
                    sessionId={sessionId}
                    eventId={eventId}
                    communityId={communityId}
                    currentStatus={session?.status}
                    speakerUserIds={speakerUserIds}
                    existingStartDate={session?.start_date}
                    existingEndDate={session?.end_date}
                    style={styles.editButton}
                  />
                )}
                {!isManagerView &&
                  isStatusScheduledOrConfirmed &&
                  isOwner &&
                  Boolean(communityId) && (
                    <SessionConfirmButton
                      sessionId={sessionId}
                      eventId={eventId}
                      communityId={communityId as string}
                      style={styles.editButton}
                    />
                  )}
                {isStatusVoting && !isManagerView && (
                  <SessionShareButton
                    canShare={canEditSession}
                    sessionId={sessionId}
                    eventId={eventId}
                    sessionStatus={session?.status}
                    style={styles.editButton}
                  />
                )}
              </>
            </View>
          </View>
          <View style={styles.contentArea}>
            <View style={[styles.speakerList, styles.bottomSpacing]}>
              {speakersToShow && speakersToShow.length > 0
                ? speakersToShow.map((speaker, index) => (
                    <React.Fragment key={speaker.user_id}>
                      {index > 0 && <View style={styles.listSeparator} />}
                      <ProfileResult
                        userId={speaker.user_id}
                        style={styles.profileResult}
                        size="small"
                      />
                    </React.Fragment>
                  ))
                : isLoadingAttendees && (
                    <View style={styles.skeletonContainer}>
                      {Array(canUseTwoColumns ? 4 : 2)
                        .fill(0)
                        .map((_, index) => (
                          <ProfileResult
                            key={`skeleton-${index}`}
                            style={styles.profileResult}
                            size="small"
                          />
                        ))}
                    </View>
                  )}
            </View>

            {isLoading ? (
              <View style={styles.loadingSkeleton}>
                <View style={styles.titleSkeleton} />
                <View style={styles.descriptionSkeleton} />
                <View style={styles.descriptionSkeleton} />
                <View style={styles.descriptionSkeleton} />
                <View style={styles.descriptionSkeleton} />
              </View>
            ) : (
              <View style={styles.titleAndDescriptionContainer}>
                <View style={styles.titleRow}>
                  <TextBold style={styles.title} numberOfLines={1}>
                    {session?.topic_name}
                  </TextBold>
                  {!isStatusScheduledOrConfirmed &&
                    formatTime(session?.start_date) && (
                      <TextReg style={styles.startTime}>
                        {formatTime(session?.start_date)}
                      </TextReg>
                    )}
                </View>
                <TextReg style={styles.description} numberOfLines={4}>
                  {session?.topic_description}
                </TextReg>
              </View>
            )}
          </View>
        </View>
      </TouchableOpacity>
    </Card>
  );
}

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    touchableWrapper: {
      flex: 1,
      width: '100%',
    },
    cardContent: {
      width: '100%',
      flex: 1,
      overflow: 'hidden',
      padding: isMobile ? 12 : 24,
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      position: 'relative',
      borderRadius: 12,
      gap: isMobile ? 16 : 20,
    },
    sessionCard: {
      minWidth: isMobile ? 0 : 200,
      maxWidth: 600,
      flex: 1,
      width: '100%',
      padding: 0,
      borderRadius: 12,
      backgroundColor: Colors.opacity05,
    },
    contentArea: {
      flex: 1,
      width: '100%',
      gap: 12,
    },
    topRow: {
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'nowrap',
      gap: 16,
      width: '100%',
      alignItems: 'center',
    },
    leftColumn: {
      flex: 1,
      flexDirection: 'column',
      gap: 8,
      minWidth: 0,
    },
    tagsWrapper: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'flex-start',
      flexWrap: 'wrap',
      gap: 8,
      minWidth: 0, // allow shrinking so action buttons fit
    },
    buttonsContainer: {
      flexDirection: 'row',
      gap: 8,
      alignItems: 'center',
      marginLeft: 'auto',
      zIndex: 100, // ensure on top
      alignSelf: 'flex-start',
      position: 'relative',
      flexShrink: 0,
    },
    editButton: {
      minWidth: 32,
      minHeight: 32,
    },
    speakerSection: {
      gap: isMobile ? 8 : 12,
      flexDirection: 'row',
      flexWrap: 'wrap',
      width: '100%',
      justifyContent: 'space-between',
    },
    profileResult: {
      width: '100%',
    },
    titleAndDescriptionContainer: {
      width: '100%',
      gap: 12,
    },
    titleRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
    },
    title: {
      fontSize: isMobile ? 20 : 24,
      flex: 1,
      marginRight: 8,
    },
    startTime: {
      fontSize: isMobile ? 12 : 14,
      color: Colors.text,
      opacity: 0.8,
      flexShrink: 0,
    },
    timeRange: {
      fontSize: isMobile ? 12 : 14,
      color: Colors.text,
      opacity: 0.8,
    },
    description: {
      fontSize: isMobile ? 14 : 16,
      paddingRight: isMobile ? 24 : 26,
    },
    speakerList: {
      width: '100%',
    },
    skeletonContainer: {
      width: '100%',
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 12,
    },
    bottomSpacing: {
      marginBottom: 12,
    },
    listSeparator: {
      height: 12,
    },
    listColumn: {
      gap: 4,
    },
    loadingSkeleton: {
      width: '100%',
    },
    titleSkeleton: {
      maxWidth: 200,
      height: isMobile ? 20 : 24,
      marginTop: isAndroid ? 3.5 : isIos ? 4.15 : isMobile ? 5 : 5.5,
      marginBottom:
        (isAndroid ? 3.5 : isIos ? 4.15 : isMobile ? 5 : 5.5) +
        (isMobile ? 8 : 4),
      borderRadius: 16,
      backgroundColor: Colors.opacity20,
    },
    descriptionSkeleton: {
      height: isMobile ? 14 : 16,
      marginVertical: isAndroid ? 0.3375 : isIos ? 2.875 : isMobile ? 3.5 : 4.5,
      borderRadius: 16,
      backgroundColor: Colors.opacity20,
      marginRight: 28,
    },
  });
